import {
  getDocumentPublishDir,
  getDocumentPublishReportPath,
  getDocumentPublishStatusPath,
  isMarkdownDocumentPath,
  resolveSingleDocPublishOutDir,
} from "./publish-utils";
import { getPlatformService } from "../platform";
import { type FileTreeNode, useEditorStore } from "../store/editor-store";
import { usePublishStore } from "../store/publish-store";
import { t as translate } from "@magic-md/i18n";

function tr(key: string): string {
  return translate("editor", key);
}

function normalizePreviewOutputPath(path: string): string {
  const normalized = path.replace(/\\/g, "/").replace(/\/+$/, "");
  return /\.html?$/i.test(normalized) ? normalized : `${normalized}/index.html`;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

async function readJsonFile(path: string): Promise<unknown> {
  try {
    const { content } = await getPlatformService().engine.workspaceReadFile({
      path,
    });
    return JSON.parse(content);
  } catch {
    return null;
  }
}

function collectPublishOutputCandidates(parsed: unknown): string[] {
  if (!parsed || typeof parsed !== "object") return [];
  const record = parsed as Record<string, unknown>;
  const result = record.result;
  const candidates = [
    readString(record.outputPath),
    result && typeof result === "object"
      ? readString((result as Record<string, unknown>).outputPath)
      : null,
  ].filter((value): value is string => !!value);
  return candidates.map(normalizePreviewOutputPath);
}

async function fileExists(path: string): Promise<boolean> {
  try {
    const { exists } = await getPlatformService().engine.workspaceExists({ path });
    return exists;
  } catch {
    return false;
  }
}

function treeContainsPublishPreviewArtifact(
  nodes: FileTreeNode[],
  publishDir: string,
): boolean {
  const normalizedPublishDir = publishDir.replace(/\/+$/, "");
  const defaultIndex = `${normalizedPublishDir}/dist/index.html`;
  const distDir = `${normalizedPublishDir}/dist`;

  for (const node of nodes) {
    if (node.path === defaultIndex || node.path === distDir) return true;
    if (node.children?.length) {
      if (treeContainsPublishPreviewArtifact(node.children, normalizedPublishDir)) {
        return true;
      }
    }
  }
  return false;
}

export function hasKnownDocumentPublishPreview(filePath: string): boolean {
  if (!isMarkdownDocumentPath(filePath)) return false;

  const task = usePublishStore.getState().fileTasks[filePath];
  if (task?.status === "completed" && task.result?.outputPath) return true;

  return treeContainsPublishPreviewArtifact(
    useEditorStore.getState().fileTree,
    getDocumentPublishDir(filePath),
  );
}

export async function resolveDocumentPublishPreviewPath(
  filePath: string,
): Promise<string | null> {
  if (!isMarkdownDocumentPath(filePath)) return null;

  const outputCandidates = [
    ...collectPublishOutputCandidates(
      await readJsonFile(getDocumentPublishReportPath(filePath)),
    ),
    ...collectPublishOutputCandidates(
      await readJsonFile(getDocumentPublishStatusPath(filePath)),
    ),
  ];

  const defaultOutDir = resolveSingleDocPublishOutDir(filePath);
  if (defaultOutDir) {
    outputCandidates.push(`${defaultOutDir}/index.html`);
  }

  for (const candidate of Array.from(new Set(outputCandidates))) {
    if (await fileExists(candidate)) return candidate;
  }
  return null;
}

export async function previewDocumentPublish(filePath: string): Promise<boolean> {
  const editor = useEditorStore.getState();
  if (!isMarkdownDocumentPath(filePath)) {
    editor.setStatusMessage(tr("publishPreview.markdownOnly"), 3000);
    return false;
  }

  const previewPath = await resolveDocumentPublishPreviewPath(filePath);
  if (!previewPath) {
    editor.setStatusMessage(tr("publishPreview.artifactMissing"), 4000);
    return false;
  }

  await useEditorStore.getState().openFileFromPath(previewPath);
  useEditorStore.getState().setViewMode("preview");
  editor.setStatusMessage(tr("publishPreview.opened"), 2000);
  return true;
}
